// @flow
/** @jsx jsx */

import * as React from 'react';

import AsyncIndicator from '@zapier/common-components/src/components/AsyncIndicator';
import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

type ButtonProps = {
  /** Background color type.
  chameleon - outlined button that changes to `color` on hover
  white - no-border white button that darkens on hover
  clear - no-border clear button that darkens on hover
  */
  backgroundType?: 'chameleon' | 'white' | 'clear',
  /** Invert colors on hover. */
  hoverInvert: boolean,
  /** Change text to upper-case. */
  casing?: 'uppercase',
  /** Visible text label for the button. */
  children: React.Node,
  /** Accessible label for the button. */
  label?: string,
  /** Color type. */
  color:
    | 'primary'
    | 'secondary'
    | 'important'
    | 'success'
    | 'platformDark'
    | 'platformLight',
  /** Size of the button. */
  size: 'small' | 'medium' | 'large',
  /** Slim height treatment of button. */
  isSlim: boolean,
  /** Changes button to a link. */
  href?: string,
  /** Disable the button. */
  isDisabled: boolean,
  /** Force button to take up full width of container. */
  isFullWidth: boolean,
  /** Restrict button to a minimum width. */
  isMinWidth: boolean,
  /** Show async indicator */
  isWorking: boolean,
  /** Callback to be executed when the button is clicked. */
  onClick?: (e: SyntheticEvent<>) => void,
  /** Link target */
  target?: string,
  /** DOM type of button tag. */
  type?: 'button' | 'submit' | 'reset',
};

const getTag = (props: ButtonProps) => {
  if (props.isDisabled || props.isWorking) {
    return 'button';
  }
  if (props.href) {
    return 'a';
  }
  return 'button';
};

const chameleonStyles = {
  dark: props => ({
    color: Colors.theme[props.color].main,
    borderColor: Colors.theme[props.color].main,
  }),
  // Light text and border would get lost on a white background, so
  // treat light buttons a little differently.
  light: props => ({
    color: Colors.theme[props.color].text,
    borderColor: Colors.theme[props.color].border,
  }),
};

const hoverStyles = props => {
  const isChameleon = props.backgroundType === 'chameleon';
  const bgDefault = !isChameleon ? 'dark' : 'main';
  const bgType = props.hoverInvert ? 'text' : bgDefault;
  const textType = props.hoverInvert ? 'main' : 'text';
  const borderType = props.hoverInvert ? 'text' : 'border';

  return {
    backgroundColor: Colors.theme[props.color][bgType],
    color: Colors.theme[props.color][textType],
    borderColor: Colors.theme[props.color][borderType],
  };
};

const getButtonCss = (props: ButtonProps) =>
  css(
    {
      alignItems: 'center',
      appearance: 'none',
      backgroundColor: Colors.theme[props.color].main,
      backgroundImage: 'none', // FIXME: FLX-71 - needed to override global zapier/zapier styles 💩
      borderColor: Colors.theme[props.color].border,
      borderRadius: 4,
      borderStyle: 'solid',
      borderWidth: 1,
      boxSizing: 'border-box',
      color: Colors.theme[props.color].text,
      cursor: 'pointer',
      display: 'inline-flex',
      fontFamily: 'Open Sans',
      justifyContent: 'center',
      lineHeight: 'normal', // FIXME: FLX-71 - needed to match web & web-ssr line-heights 💩
      margin: 0, // Safari applies 2px margin to buttons this clears it
      minWidth: 0,
      padding: '1em',
      textAlign: 'center',
      textDecoration: 'none',
      textTransform: 'none',
      transition: 'all 0.1s ease-in-out',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      width: 'auto',
      '&:focus:not([disabled]), &:hover:not([disabled])': {
        ...hoverStyles(props),
      },
      '&::-moz-focus-inner': {
        padding: 0,
        border: 0,
      },
      '&[disabled]': {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      '&:active, &:focus:not([disabled]), &:hover:not([disabled]), &[disabled]': {
        backgroundImage: 'none', // FIXME: FLX-71 - needed to override global zapier/zapier styles 💩
      },
    },
    props.size === 'small' && {
      fontSize: 12,
      height: 18,
      lineHeight: '16px',
      padding: '0 4px',
    },
    props.size === 'medium' && {
      fontSize: 14,
      minHeight: '49px',
    },
    props.size === 'large' && {
      fontSize: 15,
      minHeight: '52px',
    },
    !!props.isSlim && {
      height: 'auto',
      minHeight: 'auto',
      padding: '.5em 1.5em',
    },
    props.backgroundType === 'chameleon' && {
      backgroundColor: 'transparent',
      ...chameleonStyles[Colors.theme[props.color].type](props),
    },
    (props.backgroundType === 'clear' || props.backgroundType === 'white') && {
      color:
        Colors.theme[props.color][
          props.color === 'secondary' ? 'text' : 'main'
        ],
      backgroundColor:
        props.backgroundType === 'white' ? Colors.white : 'transparent',
      border: 'none',

      '&[disabled], &[disabled]:hover': {
        backgroundColor:
          props.backgroundType === 'white' ? Colors.white : 'transparent',
        border: 'none', // FIXME: FLX-71 - needed to override global zapier/zapier styles 💩
      },

      '&:focus:not([disabled]), &:hover:not([disabled])': {
        backgroundColor:
          props.backgroundType === 'white'
            ? Colors.storm
            : 'rgba(0, 0, 0, 0.02)',
        border: 'none', // FIXME: FLX-71 - needed to override global zapier/zapier styles 💩
        color:
          Colors.theme[props.color][
            props.color === 'secondary' ? 'text' : 'main'
          ],
      },
    },
    props.casing === 'uppercase' && {
      textTransform: 'uppercase',
    },
    props.href && {
      '&:focus:not([disabled]), &:hover:not([disabled])': {
        textDecoration: 'none',
      },
    },
    props.isFullWidth && {
      width: '100%',
    },
    props.isMinWidth && {
      minWidth: '9em',
    }
  );

export const Button = (props: ButtonProps) => {
  const Tag = getTag(props);

  const { children, label, href, isWorking, onClick, target, type } = props;

  const ariaProps = label ? { 'aria-label': label, title: label } : {};

  const rel = props.target && 'noopener noreferrer';

  return (
    <Tag
      {...{
        type,
        href,
        target,
        onClick,
        rel,
        disabled: props.isDisabled || isWorking,
      }}
      {...ariaProps}
      css={getButtonCss(props)}
    >
      {isWorking ? <AsyncIndicator color="inherit" /> : children}
    </Tag>
  );
};

Button.defaultProps = {
  color: 'secondary',
  isDisabled: false,
  isFullWidth: false,
  isMinWidth: false,
  isWorking: false,
  isSlim: false,
  hoverInvert: false,
  size: 'medium',
};

export default Button;
